import { FiBookOpen, FiCheck, FiChevronRight } from "react-icons/fi";
import { Link, useParams } from "react-router-dom";
import BackButton from "../../components/cmp/BackButton";
import { getCourseProgress, useLearningProgress } from "../store";
import type { CourseData, CourseLesson, LessonProgressMap } from "../types";
import { useCourse } from "../useCourses";
import "./course-detail.scss";
import { levelBadge, LoadingSpinner, NotFound, ProgressLabel } from "./ui";
import {useTitle} from "ahooks";
import FormattedHelmet from "../../components/cmp/intl/FormattedHelmet";

export default function CourseDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { course, isLoading, error, mutate } = useCourse(slug);
  const { progress } = useLearningProgress();

  if (isLoading) return <LoadingSpinner />;
  if (!course)
    return (
      <NotFound
        message="Course not found"
        error={error}
        onRetry={() => mutate()}
      />
    );

  const p = getCourseProgress(
    course.lessons.map((l) => l.id),
    progress,
  );

  return (
    <div>
      <BackButton/>
      <FormattedHelmet id={course?.title}/>
      <div className="card mt-2 border-0 shadow-sm mb-4 course-hero">
        <div className="d-flex flex-column flex-md-row">
          <div
            className="course-detail-cover"
            style={{ backgroundImage: `url(${course.coverUrl})` }}
          />
          <div className="p-4 course-hero-body">
            <div className="d-flex align-items-center mb-2">
              <span
                className={`badge badge-sm ${levelBadge(course.level)} me-2`}
              >
                {course.level}
              </span>
              <span className="course-meta-item">{course.category}</span>
            </div>
            <h3 className="mb-2">{course.title}</h3>
            <p className="text-muted mb-3">{course.description}</p>

            <div className="d-flex flex-wrap align-items-center gap-3 mb-2">
              <span className="course-meta-item">
                <FiBookOpen /> {course.lessons.length} lessons
              </span>
              <div
                className="flex-grow-1"
                style={{ maxWidth: 240, minWidth: 160 }}
              >
                <ProgressLabel percent={p.percent} />
              </div>
              <small className="text-muted">
                {p.done}/{p.total} completed
              </small>
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex align-items-center d-none justify-content-between mb-3">
        <h5 className="mb-0">Lessons</h5>
        <span className="course-meta-item">
          {p.done}/{p.total} completed
        </span>
      </div>

      <ul className="lesson-list">
        {course.lessons.map((lesson, index) => (
          <LessonRow
            key={lesson.id}
            course={course}
            lesson={lesson}
            index={index}
            progress={progress}
          />
        ))}
      </ul>
    </div>
  );
}

function LessonRow({
  course,
  lesson,
  index,
  progress,
}: {
  course: CourseData;
  lesson: CourseLesson;
  index: number;
  progress: LessonProgressMap;
}) {
  const lp = progress[lesson.id];
  const completed = lp?.completed;
  const score = lp?.quizScore;

  return (
    <li>
      <Link
        to={`/learning/${course.id}/${lesson.id}`}
        className="lesson-row-link"
      >
        <span className={`lesson-index${completed ? " is-done" : ""}`}>
          {completed ? <FiCheck /> : index + 1}
        </span>
        <span className="lesson-title">{lesson.title}</span>
        {completed && (
          <span className="score-pill">
            {score !== undefined ? `${score}%` : "Done"}
          </span>
        )}
        <FiChevronRight className="lesson-chevron" />
      </Link>
    </li>
  );
}
